import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { HttpTypes, SelectParams } from "@medusajs/types";

export class Cart {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * This method creates a cart. It sends a request to the [Create Cart](https://docs.medusajs.com/api/store#carts_postcarts)
   * API route.
   *
   * Related guide: [How to create a cart in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/create).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param body - The details of the cart to create.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The created cart.
   *
   * @example
   * sdk.store.cart.create({
   *   region_id: "reg_123"
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  create(
    _body: HttpTypes.StoreCreateCart,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.create() is deprecated and no longer available"
    );
  }
  /**
   * This method updates a cart. It sends a request to the
   * [Update Cart](https://docs.medusajs.com/api/store#carts_postcartsid) API route.
   *
   * Related guide: [How to update a cart in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/update).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param id - The cart's ID.
   * @param body - The data to update in the cart.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The updated cart.
   *
   * @example
   * sdk.store.cart.update("cart_123", {
   *   region_id: "reg_123"
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  update(
    _id: string,
    _body: HttpTypes.StoreUpdateCart,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.update() is deprecated. Use sdk.store.sellerCart.update() instead."
    );
  }
  /**
   * This method retrieves a cart by its ID. It sends a request to the
   * [Get Cart](https://docs.medusajs.com/api/store#carts_getcartsid) API route.
   *
   * Related guide: [How to retrieve a cart in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/retrieve).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param id - The cart's ID.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * To retrieve a cart by its ID:
   *
   * ```ts
   * sdk.store.cart.retrieve("cart_123")
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   * ```
   *
   * To specify the fields and relations to retrieve:
   *
   * ```ts
   * sdk.store.cart.retrieve("cart_123", {
   *   fields: "id,*items"
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   * ```
   *
   * Learn more about the `fields` property in the [API reference](https://docs.medusajs.com/api/store#select-fields-and-relations).
   */
  retrieve(
    _id: string,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.retrieve() is deprecated. Use sdk.store.sellerCart.retrieve() instead."
    );
  }
  /**
   * This methods adds a product variant to the cart as a line item. It sends a request to the
   * [Add Line Item](https://docs.medusajs.com/api/store#carts_postcartsidlineitems) API route.
   *
   * Related guide: [How to manage a cart's line items in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/manage-items).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param body - The details of the item to add.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.cart.createLineItem("cart_123", {
   *   variant_id: "variant_123",
   *   quantity: 1
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  createLineItem(
    _cartId: string,
    _body: HttpTypes.StoreAddCartLineItem,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.createLineItem() is deprecated and no longer available"
    );
  }
  /**
   * This method updates a line item in a cart. It sends a request to the
   * [Update Line Item](https://docs.medusajs.com/api/store#carts_postcartsidlineitemsline_id) API route.
   *
   * Related guide: [How to manage a cart's line items in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/manage-items).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param lineItemId - The line item's ID.
   * @param body - The data to update.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.cart.updateLineItem(
   *   "cart_123",
   *   "li_123",
   *   {
   *     quantity: 1
   *   }
   * )
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  updateLineItem(
    _cartId: string,
    _lineItemId: string,
    _body: HttpTypes.StoreUpdateCartLineItem,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.updateLineItem() is deprecated. Use sdk.store.sellerCart.updateLineItem() instead."
    );
  }
  /**
   * This method deletes a line item from a cart. It sends a request to the
   * [Remove Line Item](https://docs.medusajs.com/api/store#carts_deletecartsidlineitemsline_id) API route.
   *
   * Related guide: [How to manage a cart's line items in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/manage-items).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param lineItemId - The item's ID.
   * @param query - Configure the fields to retrieve in the deletion's parent cart.
   * @param headers - Headers to pass in the request.
   * @returns The deletion's details.
   *
   * @example
   * sdk.store.cart.deleteLineItem(
   *   "cart_123",
   *   "li_123"
   * )
   * .then(({ deleted, parent: cart }) => {
   *   console.log(deleted, cart)
   * })
   */
  deleteLineItem(
    _cartId: string,
    _lineItemId: string,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreLineItemDeleteResponse> {
    throw new Error(
      "sdk.store.cart.deleteLineItem() is deprecated. Use sdk.store.sellerCart.deleteLineItem() instead."
    );
  }
  /**
   * This method adds a shipping method to a cart. It sends a request to
   * the [Add Shipping Method](https://docs.medusajs.com/api/store#carts_postcartsidshippingmethods) API routes.
   *
   * Related guide: [Implement shipping step during checkout](https://docs.medusajs.com/resources/storefront-development/checkout/shipping).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param body - The shipping method's details.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.cart.addShippingMethod("cart_123", {
   *   option_id: "so_123",
   *   data: {
   *     // custom data for fulfillment provider.
   *   }
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  addShippingMethod(
    _cartId: string,
    _body: HttpTypes.StoreAddCartShippingMethods,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.addShippingMethod() is deprecated. Use sdk.store.sellerCart.addShippingMethod() instead."
    );
  }
  /**
   * This method adds promotion codes to a cart. It sends a request to the
   * [Add Promotion Code](https://docs.medusajs.com/api/store#carts_postcartsidpromotions) API route.
   *
   * Related guide: [Manage Cart Promotions in Storefront](https://docs.medusajs.com/resources/storefront-development/cart/manage-promotions).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param body - The data to add the promotions to the cart.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.cart.addPromotions("cart_123", {
   *   promo_codes: ["20OFF"]
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  addPromotions(
    _cartId: string,
    _body: HttpTypes.StoreCartAddPromotion,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.addPromotions() is deprecated. Use sdk.store.sellerCart.addPromotions() instead."
    );
  }
  /**
   * This method removes promotion codes from a cart. It sends a request to the
   * [Remove Promotion Code](https://docs.medusajs.com/api/store#carts_deletecartsidpromotions) API route.
   *
   * Related guide: [How to apply promotions to cart in the storefront](https://docs.medusajs.com/resources/storefront-development/cart/manage-items).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param body - The data to remove the promotions from the cart.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The cart's details.
   *
   * @example
   * sdk.store.cart.removePromotions("cart_123", {
   *   promo_codes: ["20OFF"]
   * })
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  removePromotions(
    _cartId: string,
    _body: HttpTypes.StoreCartRemovePromotion,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.removePromotions() is deprecated. Use sdk.store.sellerCart.removePromotions() instead."
    );
  }
  /**
   * This method completes a cart and places the order. It's the last step of the checkout flow.
   * The method sends a request to the [Complete Cart](https://docs.medusajs.com/api/store#carts_postcartsidcomplete)
   * API route.
   *
   * Related guide: [Learn how to complete cart in checkout flow](https://docs.medusajs.com/resources/storefront-development/checkout/complete-cart).
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param cartId - The cart's ID.
   * @param query - Configure the fields to retrieve in the created order.
   * @param headers - Headers to pass in the request.
   * @returns The order's details, if it was placed successfully. Otherwise, the cart is returned with an error.
   *
   * @example
   * sdk.store.cart.complete("cart_123")
   * .then((data) => {
   *   if(data.type === "cart") {
   *     // an error occurred
   *     console.log(data.error, data.cart)
   *   } else {
   *     // order placed successfully
   *     console.log(data.order)
   *   }
   * })
   */
  complete(
    _cartId: string,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCompleteCartResponse> {
    throw new Error(
      "sdk.store.cart.complete() is deprecated. Use sdk.store.sellerCart.complete() instead."
    );
  }
  /**
   * This method updates the customer of a cart.
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param id - The cart's ID.
   * @param query - Configure the fields to retrieve in the cart.
   * @param headers - Headers to pass in the request.
   * @returns The updated cart.
   *
   * @example
   * sdk.store.cart.transferCart("cart_123")
   * .then(({ cart }) => {
   *   console.log(cart)
   * })
   */
  transferCart(
    _id: string,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<HttpTypes.StoreCartResponse> {
    throw new Error(
      "sdk.store.cart.transferCart() is deprecated. Use sdk.store.sellerCart.transferCart() instead."
    );
  }
}
